
(function () {
  'use strict';

  var DEBUG = !!window.LOOP_THEME_SYNC_DEBUG;

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[loop-variant-sync]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // ---------------------------------------------------------------------
  // Frequency parsing helpers
  // ---------------------------------------------------------------------

  var WORD_NUMBERS = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    eleven: 11, twelve: 12
  };

  /**
   * Pulls a "frequency number" out of a free-text label, e.g.:
   *   "3 Pack"                 -> 3
   *   "1 Month's Supply"       -> 1
   *   "Three Month's Supply"   -> 3
   *   "Delivery every month"   -> 1
   *   "Delivery every 3 months"-> 3
   *   "One Year's Supply"      -> 12
   * Returns null when nothing usable is found.
   */
  function extractFrequencyNumber(text) {
    if (!text) return null;
    var t = String(text).toLowerCase();

    var digitMatch = t.match(/(\d+)\s*[-]?\s*(month|months|pack|packs|bottle|bottles)/);
    if (digitMatch) return parseInt(digitMatch[1], 10);

    for (var word in WORD_NUMBERS) {
      if (Object.prototype.hasOwnProperty.call(WORD_NUMBERS, word)) {
        if (t.indexOf(word + ' month') !== -1 || t.indexOf(word + '-month') !== -1 || t.indexOf(word + ' pack') !== -1) {
          return WORD_NUMBERS[word];
        }
      }
    }

    if (t.indexOf('year') !== -1) return 12;

    // "Delivery every month" / a bare "1 Pack" style label with no digit
    if (/\bmonth\b/.test(t) || /\bpack\b/.test(t) || /\bbottle\b/.test(t)) return 1;

    return null;
  }

  function getVariantFrequency(variant) {
    if (!variant) return null;
    var fromTitle = extractFrequencyNumber(variant.title);
    if (fromTitle !== null) return fromTitle;
    if (variant.options && variant.options.length) {
      return extractFrequencyNumber(variant.options.join(' '));
    }
    return null;
  }

  function getSellingPlanFrequency(sellingPlan) {
    if (!sellingPlan) return null;
    var optionText = (sellingPlan.options && sellingPlan.options[0] && sellingPlan.options[0].value) || '';
    var fromOption = extractFrequencyNumber(optionText);
    if (fromOption !== null) return fromOption;
    return extractFrequencyNumber(sellingPlan.name);
  }

  // ---------------------------------------------------------------------
  // Small data lookup helpers (all read from window.LOOP_WIDGET[productId].product,
  // which is the Shopify `product | json` blob loop-widget-standard.liquid embeds)
  // ---------------------------------------------------------------------

  function getLoopState(productId) {
    return window.LOOP_WIDGET && window.LOOP_WIDGET[productId];
  }

  function getProductData(productId) {
    var state = getLoopState(productId);
    return state && state.product;
  }

  function findVariantById(productData, variantId) {
    if (!productData || !productData.variants) return null;
    variantId = Number(variantId);
    for (var i = 0; i < productData.variants.length; i++) {
      if (Number(productData.variants[i].id) === variantId) return productData.variants[i];
    }
    return null;
  }

  /**
   * Rank a selling plan group's own plans by their frequency number,
   * ascending. With the merchant's current setup each group has exactly
   * two plans (e.g. "every 1 month" / "every 3 months", or "every 2
   * months" / "every 6 months") but this works for any number of plans.
   */
  function getGroupPlansSorted(productData, groupId) {
    var group = null;
    for (var g = 0; g < (productData.selling_plan_groups || []).length; g++) {
      if (productData.selling_plan_groups[g].id === groupId) { group = productData.selling_plan_groups[g]; break; }
    }
    if (!group) return [];

    var entries = (group.selling_plans || []).map(function (p) {
      return { plan: p, freq: getSellingPlanFrequency(p) };
    }).filter(function (x) { return x.freq != null; });

    entries.sort(function (a, b) { return a.freq - b.freq; });
    return entries;
  }

  /**
   * Rank, by pack size ascending, the variants that actually have at least
   * one of this group's plans allocated to them.
   */
  function getGroupVariantsSorted(productData, groupId) {
    var group = null;
    for (var g = 0; g < (productData.selling_plan_groups || []).length; g++) {
      if (productData.selling_plan_groups[g].id === groupId) { group = productData.selling_plan_groups[g]; break; }
    }
    if (!group) return [];

    var groupPlanIds = (group.selling_plans || []).map(function (p) { return Number(p.id); });

    var entries = (productData.variants || []).filter(function (v) {
      var allocations = v.selling_plan_allocations || [];
      return allocations.some(function (a) { return groupPlanIds.indexOf(Number(a.selling_plan_id)) !== -1; });
    }).map(function (v) {
      return { variant: v, packSize: getVariantFrequency(v) };
    }).filter(function (x) { return x.packSize != null; });

    entries.sort(function (a, b) { return a.packSize - b.packSize; });
    return entries;
  }

  /**
   * The core mapping rule: within one cadence group, the plan with the
   * SMALLEST frequency number goes with the variant with the SMALLEST pack
   * size, the plan with the largest frequency number goes with the variant
   * with the largest pack size, and so on by rank in between. This is what
   * lets cadence pairs like "1/3", "2/6", "3/9", "4/8" and "6/12 months"
   * all map onto the same "1 Pack"/"3 Packs" variants correctly without
   * hardcoding any of those specific numbers - only their relative order
   * within each group matters.
   */
  function findPlanForVariantInGroup(productData, groupId, variantId) {
    var variants = getGroupVariantsSorted(productData, groupId);
    var rank = -1;
    for (var i = 0; i < variants.length; i++) {
      if (Number(variants[i].variant.id) === Number(variantId)) { rank = i; break; }
    }
    if (rank === -1) return null;

    var plans = getGroupPlansSorted(productData, groupId);
    return plans[rank] ? plans[rank].plan : null;
  }

  /**
   * Find a {groupId, sellingPlanId} for `variantId`. Prefers staying within
   * `preferredGroupId` - the shopper's currently chosen cadence, e.g.
   * "every 1/3 months" vs "every 2/6 months" - when that group actually
   * maps this variant, so switching pack size never silently jumps the
   * shopper to a different cadence group. Only falls back to searching
   * every other group when the preferred one doesn't map this variant at
   * all (e.g. right after page load, before any group has been chosen).
   */
  function findGroupAndPlanForVariant(productData, variantId, preferredGroupId) {
    if (!productData || !productData.selling_plan_groups) return null;

    if (preferredGroupId) {
      var preferredPlan = findPlanForVariantInGroup(productData, preferredGroupId, variantId);
      if (preferredPlan) return { groupId: preferredGroupId, sellingPlanId: preferredPlan.id };
    }

    for (var g = 0; g < productData.selling_plan_groups.length; g++) {
      var groupId = productData.selling_plan_groups[g].id;
      if (groupId === preferredGroupId) continue; // already tried above
      var plan = findPlanForVariantInGroup(productData, groupId, variantId);
      if (plan) return { groupId: groupId, sellingPlanId: plan.id };
    }
    return null;
  }

  /**
   * The reverse lookup: given a selling plan id the shopper just picked in
   * the widget, find the variant sharing its rank within that plan's own
   * group (see findPlanForVariantInGroup above for the ranking rule). A
   * given sellingPlanId belongs to exactly one group, so there's no
   * "preferred group" ambiguity here.
   */
  function findVariantForSellingPlan(productData, sellingPlanId) {
    if (!productData || !productData.selling_plan_groups) return null;
    sellingPlanId = Number(sellingPlanId);

    for (var g = 0; g < productData.selling_plan_groups.length; g++) {
      var groupId = productData.selling_plan_groups[g].id;
      var plans = getGroupPlansSorted(productData, groupId);
      var rank = -1;
      for (var i = 0; i < plans.length; i++) {
        if (Number(plans[i].plan.id) === sellingPlanId) { rank = i; break; }
      }
      if (rank === -1) continue;

      var variants = getGroupVariantsSorted(productData, groupId);
      return variants[rank] ? variants[rank].variant : null;
    }
    return null;
  }

  // ---------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------

  function dispatchChange(el) {
    var evt;
    try {
      evt = new Event('change', { bubbles: true });
    } catch (e) {
      evt = document.createEvent('Event');
      evt.initEvent('change', true, true);
    }
    el.dispatchEvent(evt);
  }

  function decodeHtmlEntities(str) {
    var txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  /**
   * The authoritative "currently selected variant id" for a product.
   * We deliberately do NOT trust window.LOOP_WIDGET[productId].selectedVariantId
   * here: Loop only ever populates that field by looking for
   * `input[name="id"]` inside the product form (see
   * LOOP_WIDGET_VARIANT_ID_SELECTORS / setSelectedVariantFromForm in
   * loop-widget.js), but this theme uses `<select class="product-actual-select"
   * name="id">` instead of an `<input>`, so that field is silently never
   * set. The hidden select itself is the real source of truth here.
   */
  function getCurrentVariantId(productId) {
    var root = findFeaturedProductRoot(productId);
    var select = root && root.querySelector('.product-actual-select');
    if (select && select.value) return select.value;
    var state = getLoopState(productId);
    return (state && state.selectedVariantId) || null;
  }

  function findFeaturedProductRoot(productId) {
    var roots = document.querySelectorAll('featured-product');
    for (var i = 0; i < roots.length; i++) {
      var jsonEl = roots[i].querySelector('[data-product-json]');
      if (!jsonEl) continue;
      try {
        var data = JSON.parse(jsonEl.textContent);
        if (Number(data.id) === Number(productId)) return roots[i];
      } catch (e) { /* ignore malformed json */ }
    }
    return null;
  }

  // ---------------------------------------------------------------------
  // Direction 1: Loop widget -> select the correct delivery frequency
  // ---------------------------------------------------------------------

  /**
   * Whether we should be driving the frequency selector at all right now.
   * We stay out of the way once the shopper has explicitly chosen
   * "one-time purchase" - Loop remembers that choice on its own
   * (previousPurchaseOption) and also remembers the last selling plan
   * chosen, so switching back to subscription later restores it correctly
   * without our help.
   */
  function shouldDriveFrequency(productId) {
    var state = getLoopState(productId);
    if (!state) return false;
    return state.previousPurchaseOption !== 'one-time';
  }

  function selectLoopFrequency(productId, groupId, sellingPlanId) {
    var container = document.getElementById('loop-widget-container-id-' + productId);
    if (!container) return false;

    var groupContainer = container.querySelector('#loop-widget-purchase-option-id-' + groupId);
    if (!groupContainer) return false;

    var state = getLoopState(productId) || {};

    // Step 1: make sure the right purchase-option (subscription) group is
    // the active one. Clicking the group wrapper itself (not a frequency
    // button/select inside it) makes Loop select the group and its own
    // default plan - see the click handler in loop-widget.js.
    if (String(state.selectedSellingPlanGroupId) !== String(groupId)) {
      groupContainer.click();
    }

    // Step 2: force the exact frequency we want, overriding whatever
    // default Loop just picked in step 1.
    var select = groupContainer.querySelector('.loop-widget-sp-selector');
    if (select) {
      if (String(select.value) !== String(sellingPlanId)) {
        select.value = String(sellingPlanId);
        dispatchChange(select);
        log('Selected frequency via dropdown', productId, sellingPlanId);
      }
      return true;
    }

    var button = groupContainer.querySelector('#loop-widget-sp-button-id-' + sellingPlanId);
    if (button) {
      if (!button.classList.contains('loop-widget-sp-button-selected')) {
        button.click();
        log('Selected frequency via button', productId, sellingPlanId);
      }
      return true;
    }

    return false;
  }

  function syncFrequencyToVariant(productId, variantId) {
    var productData = getProductData(productId);
    if (!productData) return;

    var variant = findVariantById(productData, variantId);
    if (!variant) return;

    if (!shouldDriveFrequency(productId)) {
      log('Skipping frequency sync - shopper is on one-time purchase', productId);
      return;
    }

    var state = getLoopState(productId);
    var preferredGroupId = state && state.selectedSellingPlanGroupId;

    var match = findGroupAndPlanForVariant(productData, variantId, preferredGroupId);
    if (!match) {
      // No selling plan mapped to this variant in any group. Nothing to
      // select - Loop's own skeleton-loader logic already hides the widget
      // for variants with no selling plan groups at all.
      log('No matching selling plan for variant', variantId, '- leaving as-is');
      return;
    }

    selectLoopFrequency(productId, match.groupId, match.sellingPlanId);
  }

  // ---------------------------------------------------------------------
  // Direction 2: pack/variant selector -> tell Loop about the new variant
  // ---------------------------------------------------------------------

  function switchThemeVariant(productId, variantId) {
    var root = findFeaturedProductRoot(productId);
    if (!root) return false;

    var productData = getProductData(productId);
    var variant = findVariantById(productData, variantId);
    if (!variant || !variant.options) return false;

    var optionGroups = root.querySelectorAll('.product-single-option[data-option-index]');
    var appliedSomething = false;

    optionGroups.forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index'), 10);
      var desiredValue = variant.options[idx - 1];
      if (desiredValue === undefined) return;

      var labels = group.querySelectorAll('.option-label');
      if (!labels.length) return;

      var target = null;
      labels.forEach(function (lbl) {
        if (target) return;
        var val = lbl.getAttribute('data-value') || '';
        if (val === desiredValue || decodeHtmlEntities(val) === desiredValue) {
          target = lbl;
        }
      });

      if (target && !target.classList.contains('active')) {
        target.click();
        appliedSomething = true;
      }
    });

    return appliedSomething;
  }

  function handleThemeVariantChange(productId, variantId) {
    if (!productId || !variantId) return;
    if (!window.LoopSubscriptions || typeof window.LoopSubscriptions.handleLoopWidgetVariantIdChange !== 'function') return;
    if (!getLoopState(productId)) return;

    // Loop's own mutation-observer based detection does not fire for this
    // theme's swatch/pack picker (it sets the hidden select's `.value`
    // property, not a DOM attribute), so we drive it explicitly.
    window.LoopSubscriptions.handleLoopWidgetVariantIdChange(productId, variantId);
    log('Told Loop about variant change', productId, variantId);

    syncFrequencyToVariant(productId, variantId);
  }

  function syncFromThemeToLoop(root) {
    var jsonEl = root.querySelector('[data-product-json]');
    var actualSelect = root.querySelector('.product-actual-select');
    if (!jsonEl || !actualSelect) return;

    var productData;
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    var productId = productData.id;
    var variantId = actualSelect.value;
    if (!productId || !variantId) return;

    handleThemeVariantChange(productId, variantId);
  }

  function syncVariantToFrequency(productId) {
    var state = getLoopState(productId);
    if (!state) return;

    var productData = state.product;
    if (!productData) return;

    var sellingPlanId = state.selectedSellingPlanId;
    if (!sellingPlanId) return; // one-time selected, or nothing selected yet

    var currentVariantId = getCurrentVariantId(productId);

    var targetVariant = findVariantForSellingPlan(productData, sellingPlanId);
    if (!targetVariant || Number(targetVariant.id) === Number(currentVariantId)) return;

    switchThemeVariant(productId, targetVariant.id);
  }

  // ---------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------
  //
  // IMPORTANT: Loop's own click handler for a frequency button/select
  // (handleLoopWidgetSellingPlanChange) calls event.stopPropagation() as
  // its very first line. A normal bubble-phase listener on `document`
  // therefore NEVER sees that click - it gets cut off at the button before
  // it can bubble any further. To work around this reliably, every
  // listener below is registered in the CAPTURE phase (the trailing
  // `true`). Capture-phase listeners on an ancestor always run before the
  // event reaches its target and before any bubble-phase stopPropagation
  // downstream can take effect, so this can't be blocked the same way.
  // The actual sync work is still deferred with setTimeout(0) so Loop's
  // (or the theme's) own bubble-phase handler finishes updating its state
  // first; we just read the result of that afterwards.

  // 1) Shopper clicks a pack/variant swatch (".option-label") ------------
  document.addEventListener('click', function (event) {
    var label = event.target.closest ? event.target.closest('.option-label') : null;
    if (!label) return;
    var root = label.closest('featured-product');
    if (!root) return;
    setTimeout(function () { syncFromThemeToLoop(root); }, 0);
  }, true);

  // 1b) Fallback: a plain <select class="option-selector"> is used instead
  //     of swatches (settings.product_option_style != 'swatch').
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.classList || !target.classList.contains('option-selector')) return;
    var root = target.closest('featured-product');
    if (!root) return;
    setTimeout(function () { syncFromThemeToLoop(root); }, 0);
  }, true);

  // 2) Shopper picks a delivery frequency in the Loop widget -------------
  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest('.loop-widget-sp-button') : null;
    if (!btn) return;
    var container = btn.closest('[id^="loop-widget-container-id-"]');
    if (!container) return;
    var productId = container.id.replace('loop-widget-container-id-', '');
    setTimeout(function () { syncVariantToFrequency(productId); }, 0);
  }, true);

  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.classList || !select.classList.contains('loop-widget-sp-selector')) return;
    var container = select.closest('[id^="loop-widget-container-id-"]');
    if (!container) return;
    var productId = container.id.replace('loop-widget-container-id-', '');
    setTimeout(function () { syncVariantToFrequency(productId); }, 0);
  }, true);

  // 2b) Shopper switches the purchase option INTO a subscription group
  //     (e.g. clicking back from "One-time purchase" to "Subscribe & save")
  //     without picking a specific frequency in that same click. Loop's own
  //     default-plan logic for this case (previousSelectedSellingPlanId)
  //     just restores whichever frequency was last selected, with no idea
  //     that the shopper may have changed the pack size in the meantime
  //     (this is exactly the "3-pack + every-3-months -> one-time -> switch
  //     to 1-pack -> back to subscription" case, which would otherwise stay
  //     on "every 3 months"). Re-run the same frequency correction here,
  //     once Loop's own group-selection + default-plan logic has finished.
  document.addEventListener('click', function (event) {
    var groupEl = event.target.closest ? event.target.closest('[data-loop-widget-selling-plan-group]') : null;
    if (!groupEl) return;
    // A click that actually landed on the frequency button/select itself is
    // already handled above by listener (2) - the shopper is explicitly
    // picking a different frequency there, so this must not override it.
    if (event.target.closest('.loop-widget-sp-button') || event.target.closest('.loop-widget-sp-selector')) return;
    var container = groupEl.closest('[id^="loop-widget-container-id-"]');
    if (!container) return;
    var productId = container.id.replace('loop-widget-container-id-', '');
    setTimeout(function () {
      var variantId = getCurrentVariantId(productId);
      if (variantId) syncFrequencyToVariant(productId, variantId);
    }, 0);
  }, true);

  // 3) Initial page load ---------------------------------------------------
  // Loop dispatches a "loopWidgetLoaded" CustomEvent on `document` every
  // time it finishes rendering for a variant that has selling plans
  // (see handleLoopWidgetSkeletonLoader / dispatchLoopWidgetEvent in
  // loop-widget.js) - this fires on the very first render (page load) AND
  // again every time handleLoopWidgetVariantIdChange runs. That makes it
  // the single reliable hook for "make sure the widget matches whatever
  // variant is currently showing", covering the case the person loads the
  // product page directly on a pack size whose delivery frequency Loop
  // defaulted incorrectly.
  document.addEventListener('loopWidgetLoaded', function (event) {
    var productId = event && event.detail && event.detail.productId;
    if (!productId) return;
    var variantId = getCurrentVariantId(productId);
    if (!variantId) return;
    setTimeout(function () { syncFrequencyToVariant(productId, variantId); }, 0);
  }, false);
})();